import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from 'urql';
import { Q_ME } from '@/lib/queries';

/**
 * Resolves the active workspace before rendering the app.
 *
 * `me` works without an X-Workspace-Id (the api bootstraps to the user's first
 * workspace). We read `currentWorkspaceId` from the response, persist it, and
 * only then render children — so every subsequent query carries the header.
 */
export function WorkspaceGate({ children }: { children: ReactNode }) {
  // Always resolve against `me` — never blindly trust a stored id, which may be
  // stale after a DB reseed and would silently return zero projects.
  const [ready, setReady] = useState(false);
  const [{ data, fetching, error }] = useQuery({ query: Q_ME });

  useEffect(() => {
    const me = data?.me;
    if (!me) return;
    const stored = localStorage.getItem('workspace_id');
    const valid = (me.workspaces ?? []).some((w: any) => w.id === stored);
    const ws = valid ? stored : me.currentWorkspaceId;
    if (ws) {
      localStorage.setItem('workspace_id', ws);
      setReady(true);
    }
  }, [data]);

  if (ready) return <>{children}</>;

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-center px-6">
        <p className="text-red-600 font-medium">Couldn't load your workspace.</p>
        <p className="text-sm text-stone-500">{error.message}</p>
        <button onClick={() => location.reload()} className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium">Retry</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center text-stone-500">
      {fetching ? 'Setting up your workspace…' : 'Loading…'}
    </div>
  );
}
